import React, { useRef, useState, DragEvent, ChangeEvent, useEffect } from 'react';
import { AiOutlineCloudUpload } from 'react-icons/ai';

interface ImageUploaderProps {
  onImageUpload: (file: File) => void;
  onImageRemove?: () => void;
  previewUrl?: string | null;
  title?: string;
  subTitle?: string;
  dragActiveTitle?: string;
  className?: string;
  height?: string;
  acceptedFileTypes?: string;
  disabled?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  onImageUpload,
  onImageRemove,
  previewUrl = null,
  title = 'Drop image here, or click to upload',
  subTitle,
  dragActiveTitle = 'Drop image here',
  className = '',
  height = 'h-44',
  acceptedFileTypes = 'image/*',
  disabled = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Log when previewUrl changes
  useEffect(() => {
    if (previewUrl) {
      console.log('ImageUploader received previewUrl:', previewUrl);
    }
  }, [previewUrl]);

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const file = e.target.files?.[0];
    if (file) {
      onImageUpload(file);
    }
  };

  const handleDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const file = e.dataTransfer.files?.[0];
    if (file && file.type.match(acceptedFileTypes.replace('/*', '/.'))) {
      onImageUpload(file);
    }
  };

  const handleClickUpload = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleRemoveImage = (e: React.MouseEvent) => {
    if (disabled) return;
    
    e.stopPropagation();
    if (onImageRemove) {
      onImageRemove();
    }
  };

  return (
    <div
      className={`flex flex-col gap-4 justify-center border-2 ${
        disabled
          ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-70'
          : isDragging
            ? 'border-blue-500 bg-blue-50'
            : previewUrl
              ? 'border-green-500'
              : 'border-gray-300'
      } ${height} rounded-lg items-center ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'} relative overflow-hidden transition-colors ${className}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClickUpload}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
        accept={acceptedFileTypes}
      />

      {previewUrl ? (
        <div className="relative w-full h-full flex items-center justify-center">
          <img 
            src={previewUrl} 
            alt="Uploaded" 
            className="max-h-full max-w-full object-contain" 
          />
          {onImageRemove && !disabled && (
            <button
              onClick={handleRemoveImage}
              className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
              aria-label="Remove image"
            >
              ✕
            </button>
          )}
          {onImageRemove && disabled && (
            <div 
              className="absolute top-2 right-2 bg-gray-400 text-white rounded-full p-1 cursor-not-allowed opacity-60"
              aria-label="Remove image (disabled)"
            >
              ✕
            </div>
          )}
        </div>
      ) : (
        <>
          <div className="text-[#238BFF] text-6xl">
            <AiOutlineCloudUpload />
          </div>
          <div className="text-[#999999] font-inter text-center px-4">
            {isDragging ? dragActiveTitle : title}
          </div>
          {subTitle && <div className="text-gray-500 text-xs">{subTitle}</div>}
        </>
      )}
    </div>
  );
};

export default ImageUploader;
